import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Statistic, Table, Tag, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { StepFooter } from '../StepFooter';
import { productApi } from '../../../api';

/**
 * Setup step 6 — Invite guardians. Reads the guardians already on file from
 * students created in step 1. Real SMS delivery ships with the Comms module;
 * this screen previews who *would* be notified when that lands.
 *
 * The list is derived from students: every student's primary guardian shows
 * once. Sibling detection (same phone, two students) is a follow-up.
 */
export function GuardiansSetupPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const list = useQuery({
    queryKey: ['students', institution, {}],
    queryFn: () => productApi.listStudents(institution),
    enabled: !!institution,
  });

  const withPrimary = list.data?.rows.filter((r) => r.primary_guardian) ?? [];
  const withoutPrimary = (list.data?.rows.length ?? 0) - withPrimary.length;
  const uniquePhones = new Set(withPrimary.map((r) => r.primary_guardian!.phone)).size;

  const canMarkDone = withPrimary.length > 0;

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 14 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Invite guardians
        </Typography.Title>
        <Typography.Text type="secondary">
          Deliberately last. A parent's first impression is worth protecting — we invite only after
          everything else is right.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 14 }}>
        <Card size="small">
          <Statistic title="Students on file" value={list.data?.total ?? 0} />
        </Card>
        <Card size="small">
          <Statistic title="With primary guardian" value={withPrimary.length} />
        </Card>
        <Card size="small">
          <Statistic title="Unique phone numbers" value={uniquePhones} suffix={<Typography.Text type="secondary" style={{ fontSize: 11 }}>would receive SMS</Typography.Text>} />
        </Card>
      </div>

      {withoutPrimary > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${withoutPrimary} student${withoutPrimary === 1 ? ' has' : 's have'} no primary guardian on file — they'll be skipped.`}
        />
      )}

      <Card size="small" title="Preview: who would receive an invitation">
        {withPrimary.length === 0 ? (
          <Empty
            description={
              <span>
                No guardians on file yet. Go back to <a href={`/i/${institution}/setup/students`}>Step 1</a>
                {' '}and add students first.
              </span>
            }
            style={{ padding: 16 }}
          />
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.id}
            dataSource={withPrimary}
            columns={[
              {
                title: 'Guardian',
                key: 'g',
                render: (_, r) => r.primary_guardian?.name ?? '—',
              },
              {
                title: 'Phone',
                key: 'phone',
                render: (_, r) => (
                  <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>
                    {r.primary_guardian?.phone}
                  </span>
                ),
              },
              {
                title: 'Relation',
                key: 'rel',
                render: (_, r) => <Tag>{r.primary_guardian?.relation}</Tag>,
              },
              { title: 'For student', dataIndex: 'name', key: 'name' },
              {
                title: 'Grade',
                key: 'grade',
                render: (_, r) => `${r.grade}${r.section ? ` · ${r.section}` : ''}`,
              },
            ]}
          />
        )}
      </Card>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12 }}
        message={
          <span>
            <b>Sending isn't real yet.</b> The Comms module wires up SMS + WhatsApp templates
            (C-08). This step exists so you can confirm the roster looks right before that lands.
          </span>
        }
      />

      <div style={{ marginTop: 12 }}>
        <Button icon={<UserOutlined />} disabled title="SMS delivery ships with the Comms module">
          Send invitations
        </Button>
      </div>

      <StepFooter
        institution={institution}
        step="invite_guardians"
        summary={`${withPrimary.length} guardian${withPrimary.length === 1 ? '' : 's'} on the list`}
        disabled={!canMarkDone}
        disabledReason="Add students with guardians first"
      />
    </div>
  );
}
